import { createSlice, nanoid, PayloadAction } from '@reduxjs/toolkit';
import { AlertColor } from '../../types/ToastTypes';

export interface NotificationItem {
  id: string;
  message: string;
  type: AlertColor;
  /** Epoch millis, used to show relative time in the panel. */
  timestamp: number;
  read: boolean;
}

export interface NotificationState {
  /** Newest first. Rendered in the header notification panel. */
  items: NotificationItem[];
}

const initialState: NotificationState = {
  items: [],
};

// Keep the history bounded so a long session can't grow it without limit.
const MAX_NOTIFICATIONS = 50;

type AddNotificationInput = {
  message: string;
  type?: AlertColor;
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: {
      reducer: (state, action: PayloadAction<NotificationItem>) => {
        state.items.unshift(action.payload);
        if (state.items.length > MAX_NOTIFICATIONS) {
          state.items.length = MAX_NOTIFICATIONS;
        }
      },
      // Generate the id/timestamp here so callers only pass the message.
      prepare: ({ message, type = 'danger' }: AddNotificationInput) => ({
        payload: {
          id: nanoid(),
          message,
          type,
          timestamp: Date.now(),
          read: false,
        } as NotificationItem,
      }),
    },
    markAllNotificationsRead: (state) => {
      state.items.forEach((item) => {
        item.read = true;
      });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.items = [];
    },
  },
});

export const {
  addNotification,
  markAllNotificationsRead,
  removeNotification,
  clearNotifications,
} = notificationSlice.actions;

export const notificationReducer = notificationSlice.reducer;
